// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Contabil/CentralFiscal.tsx
//  Central Fiscal do cliente: a mesma análise que o contador vê no Portal do Contador
//  (Simples Nacional, alertas, CFOP/CST/PIS-COFINS/NCM), sobre a empresa do login.
//  Os números vêm do Cofre Fiscal (documentos autorizados copiados dos XMLs).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { EnviarXmlsBotao, type ResumoEnvio } from '@/features/cofre/EnviarXmlsBotao';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { apiClient } from '@/lib/apiClient';
import { useAuth } from '@/hooks/useAuth';
import { AnaliseFiscalPainel } from '@/features/cofre/AnaliseFiscalPainel';
import type { AnaliseFiscal } from '@/features/cofre/analiseFiscal';
import { competenciaAtual, rotuloCompetencia, ultimasCompetencias } from '@/features/contador/contadorService';

async function enviarXmls(arquivo: File) {
  const form = new FormData();
  form.append('arquivo', arquivo);
  const r = await apiClient.post<ResumoEnvio>('/api/contabil/central-fiscal/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600_000,
  });
  return r.data;
}

async function carregar(competencia: number, anexo?: number) {
  const r = await apiClient.get<AnaliseFiscal>('/api/contabil/central-fiscal/analises', { params: { competencia, anexo } });
  return r.data;
}

export default function CentralFiscal() {
  const { user } = useAuth();
  const [competencia, setCompetencia] = useState(competenciaAtual());
  const qc = useQueryClient();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Central Fiscal</h1>
          <p className="text-muted-foreground mt-1">
            Simples Nacional, pontos de atenção e resumos fiscais de {user?.empresa?.nomeFantasia ?? user?.empresa?.razaoSocial ?? 'sua empresa'}
          </p>
        </div>
        <div className="sm:ml-auto flex gap-2">
          <EnviarXmlsBotao enviar={enviarXmls} rotulo="Enviar XMLs antigos"
            aoConcluir={() => qc.invalidateQueries({ queryKey: ['central-fiscal'] })} />
          <Select value={String(competencia)} onValueChange={(v) => setCompetencia(Number(v))}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ultimasCompetencias().map((c) => <SelectItem key={c} value={String(c)}>{rotuloCompetencia(c)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <AnaliseFiscalPainel chave={['central-fiscal', user?.empresa?.id]} competencia={competencia} carregar={carregar} />
    </div>
  );
}
